import {Storage} from "@/services/Storage";
import axios from "axios";

export default {
    name: 'stock-transfer',
    namespaced: true,
    state: {
        allStores : [],
        franchiseSetting:null,
        itemsToSend : []
    },
    getters: {
        getStores : (state) => state.allStores,
        getFranchiseSettings: (state) => state.franchiseSetting,
        getItemsToSend : (state) => state.itemsToSend,
        /**
         * Return the first store flagged as `IsMain === true` from
         * `state.allStores`, or `undefined` when no store in the current
         * group carries that flag. Used by the send-side receiving-store
         * selector to pin a "Main Store" card at the top of the list so
         * cashiers can route transfers back to head office in one tap.
         *
         * @param {object} state - Vuex state for this module
         * @returns {object|undefined} the main store object, or undefined
         */
        getMainStore: (state) => {
            if (!Array.isArray(state.allStores)) return undefined;
            // The Main Store is the franchise head office, identified by
            // the `mainBranch === true` flag in the store object returned
            // by `franchiseTomcatApi/stores/getByGroup`. (Earlier drafts
            // used `IsMain` but production data uses `mainBranch`.)
            return state.allStores.find(s => s && s.mainBranch === true);
        },
    },
    mutations: {
        setFranchiseSettings : async (state, payload) =>{
            if (!payload){
                payload = await Storage.getFranchiseSettings();
            }
            state.franchiseSetting= payload;
        },
        setAllStores : async (state,payload) => {
            if (!payload){
                let stores = await Storage.getAllStores();
                if (!stores){
                    let defaultStore = await Storage.getDefaultStore();
                    stores = [];
                    stores.push(defaultStore);
                }
                payload = stores;
            }else{
                await Storage.setAllStores(payload);
            }
            state.allStores = payload;
        },
        /**
         * Append a new line to the in-progress transfer.
         *
         * CRITICAL: every payload MUST carry a unique `index` value. The
         * `updateItemToSend` and `removeFromItemsToSend` mutations locate
         * their target row by `item.index === index`; if `index` is
         * undefined for every row, the strict-equality check matches
         * ALL rows and `.map()` replaces the entire list with a single
         * `updatedItem` reference (the "editing one item updates all
         * of them" bug).
         *
         * Callers should read `index` from
         * `getters.getItemsToSend.length` INSIDE the commit loop so each
         * row gets a fresh value as the array grows.
         *
         * @param {object} state - Vuex state for this module
         * @param {object} payload - row to push, MUST include a unique `index`
         */
        addToItemsToSend: (state, payload) => state.itemsToSend.push(payload),
        /**
         * Remove a row from `state.itemsToSend` by its unique `index`.
         *
         * IMPORTANT: the second argument is the row's `index` value, NOT
         * its PLU. Filtering by `Plu` (the previous behaviour) breaks the
         * moment the same PLU is added twice in the same transfer - both
         * rows get dropped together. This mirrors the lytning-core fix.
         *
         * @param {object} state - Vuex state for this module
         * @param {number} index - unique index of the row to remove
         */
        removeFromItemsToSend: (state, index) => {
            state.itemsToSend = state.itemsToSend.filter(item => item.index !== index);
        },
        clearItemsToSend : (state) => state.itemsToSend = [],
        /**
         * Upsert a row in `state.itemsToSend` by its unique `index`.
         *
         * If a row with the given `index` already exists it is replaced
         * with `{ ...item, ...updatedItem, index }`; otherwise the new
         * row is appended. Locate the target by `item.index === index`
         * (NOT by `Plu`) - see the JSDoc on `addToItemsToSend` for the
         * "editing one item updates all of them" bug that follows if
         * the index invariant is violated by callers.
         *
         * @param {object} state - Vuex state for this module
         * @param {object} change - `{ index, ...updatedItem }`
         */
        updateItemToSend: (state, { index, ...updatedItem }) => {
            if (state.itemsToSend.some(item => item.index === index)) {
                state.itemsToSend = state.itemsToSend?.map(item =>
                    item.index === index ? { ...item, ...updatedItem, index } : item
                );
            } else {
                state.itemsToSend.push({ ...updatedItem, index });
            }
        },
    },
    actions: {
        getAllFranchiseSettings: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `franchise_settings/${store.StoreDB}`)
                .then(({data})=>context.commit(`setFranchiseSettings`,data))
                .catch(err=>errorHandler.tomcatError(err))
        },
        getAllStores: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi+`stores/getByGroup`,{
                params : { group : store.Group }
            }).then(({data})=>context.commit(`setAllStores`,data.sort((a, b) => a.StoreName.localeCompare(b.StoreName))));
        },
        getIncomingTransfers: async (context,payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi+`franchise_stockpending/${store.StoreDB}/search`,payload)
                .then(({data}) => data)
                .catch(err=>errorHandler.tomcatError(err))
        },
        /**
         * Fetch the lines for a single in-flight transfer by document
         * number. Reuses the existing `getIncomingTransfers` endpoint
         * (which already returns the rows for the supplied `DocumentNumber`
         * filter) and narrows the result to the one document the caller
         * asked for.
         *
         * @param {object} context - Vuex action context
         * @param {string|number} documentNumber - transfer document number
         * @returns {Promise<Array|undefined>} matching transfer lines, or
         *      `undefined` when the current store DB cannot be resolved
         */
        getItemsByDocumentNumber: async (context, documentNumber) => {
            let storeDB = await Storage.getCurrentStoreDB();
            if (!storeDB) return;
            const all = await context.dispatch('getIncomingTransfers', { DocumentNumber: documentNumber });
            return Array.isArray(all) ? all.filter(t => String(t.DocumentNumber) === String(documentNumber)) : all;
        },
        getTransferByDocument: async (context, payload)=> {
            // Accept either a primitive document number (legacy) or the
            // `{ documentNumber, senderID }` object that the
            // `ViewIncomingTransfer` component actually dispatches.
            // Without this normalisation, the template literal below
            // stringifies the object to `[object Object]` and the
            // backend returns HTTP 400.
            const { documentNumber, senderID } =
                (payload && typeof payload === 'object')
                    ? payload
                    : { documentNumber: payload, senderID: undefined };

            let store = await Storage.getDefaultStore();
            // The lytningtomcat backend (and the local dev mirror) scope
            // the document by sender id, so `?senderID=...` is required.
            // When the caller didn't supply one (e.g. the row's `from`
            // field was undefined and the route query collapsed to ''),
            // fall back to the current store's CustomerID so the URL
            // is always well-formed and the backend doesn't 400.
            const effectiveSenderID = (senderID !== undefined && senderID !== null && senderID !== '')
                ? senderID
                : (store && store.CustomerID !== undefined ? store.CustomerID : '');
            const url = franchiseTomcatApi
                + `franchise_transfer/${store.StoreDB}/transferLine/${documentNumber}`
                + (effectiveSenderID !== '' && effectiveSenderID !== undefined && effectiveSenderID !== null
                    ? `?senderID=${encodeURIComponent(effectiveSenderID)}`
                    : '');

            return axios.get(url)
                .then(({data})=>data)
                .catch(err=>errorHandler.tomcatError(err))
        },

        rejectTransferByDocumentNumber : async (context , payload) => {
            // Accept either a bare `{ reason, documentNumber }` (legacy)
            // or the new `{ reason, documentNumber, senderID }` shape that
            // `ViewIncomingTransfer.rejectTransfer` dispatches. The
            // sender id is required by the lytningtomcat backend to scope
            // the rejection to the correct pending document.
            const { reason, documentNumber, senderID } =
                (payload && typeof payload === 'object')
                    ? payload
                    : { reason: undefined, documentNumber: undefined, senderID: undefined };

            let store = await Storage.getDefaultStore();
            const body = {
                reason,
                documentNumber,
                ...(senderID !== undefined && senderID !== null && senderID !== ''
                    ? { senderID }
                    : {}),
            };
            return axios.post(franchiseTomcatApi+`franchise_stockpending/${store.StoreDB}/rejectTransferByDocumentNumber`,body)
                .then(()=>context.dispatch(`getAllStock`))
                .catch(err=>errorHandler.tomcatError(err,true))
        },

        acceptTransferByDocumentNumber : async (context , arg) => {
            // Accept either a primitive `documentNumber` (legacy callers)
            // or the `{ documentNumber, senderID }` object that
            // `ViewIncomingTransfer.acceptTransfer` actually dispatches.
            // Without `senderID`, the lytningtomcat backend returns
            // HTTP 400 because it cannot scope the document to a
            // sender.
            const { documentNumber, senderID } =
                (arg && typeof arg === 'object')
                    ? arg
                    : { documentNumber: arg, senderID: undefined };

            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser()
            let settings = await Storage.getDeviceSettings()
            const payload = {
                documentNumber : documentNumber,
                user : user.Username,
                pc : settings.PC,
                ...(senderID !== undefined && senderID !== null && senderID !== ''
                    ? { senderID }
                    : {}),
            }
            return axios.post(franchiseTomcatApi+`franchise_stocksent/${store.StoreDB}/acceptTransferByDocumentNumber`,payload)
                .then(({data})=> {
                    return Promise.all([
                        context.dispatch(`printGRV`,data),
                        context.dispatch(`getAllStock`)
                    ])
                }).catch(err=>errorHandler.tomcatError(err,true))
        },
        /**
         * Initiate (commit) the in-progress transfer against the receiving
         * store and clear the local `itemsToSend` queue on success.
         *
         * Accepts either a scalar `storeID` (legacy callers) or an object
         * `{ storeID, saleNum }` (the new send-side flow). `saleNum` is the
         * current draft / sale number shown to the cashier and is forwarded
         * to the backend so the transfer can be tied back to its draft.
         *
         * @param {object} context - Vuex action context
         * @param {string|number|object} arg - scalar storeID, or
         *      `{ storeID, saleNum }` payload
         * @returns {Promise<*>} server response body, or
         *      `errorHandler.tomcatError` result on failure
         */
        transferStock: async (context, arg) => {
            const storeID = (arg && typeof arg === 'object') ? arg.storeID : arg;
            const saleNum = (arg && typeof arg === 'object') ? arg.saleNum : undefined;
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            let obj = {
                storeID: storeID,
                saleNum: saleNum,
                items: context.getters.getItemsToSend,
                user: user.Username,
                pc: settings.PC,
            };
            return axios.post(franchiseTomcatApi + `franchise_stocksent/${store.StoreDB}/inititateTransfer`, obj)
                .then((res) => {
                    context.commit('clearItemsToSend');
                    // Backend returns the document number as the response body
                    // (matches lytning-core behaviour) - resolve with it so
                    // the UI can route to the printed transfer.
                    return res && res.data !== undefined ? res.data : res;
                })
                .catch(err => errorHandler.tomcatError(err, true));
        },
        /**
         * Persist the in-progress transfer as a draft (saved, not sent)
         * against the receiving `storeID`. The optional `description` is
         * the user-supplied note shown in the "Saved" list. Items are
         * taken from `getters.getItemsToSend` exactly as the cashier
         * composed them, including their unique `index` values.
         *
         * @param {object} context - Vuex action context
         * @param {object} args
         * @param {string|number} args.storeID - receiving store customer id
         * @param {string|number} args.saleNum - document / sale number
         * @param {string} [args.description] - user-supplied note
         * @returns {Promise<*>} server response body, or `errorHandler.tomcatError` result on failure
         */
        saveStockTransfer: async (context, { storeID, saleNum, description }) => {
            let storeDB = await Storage.getCurrentStoreDB();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            const payload = {
                storeID,
                saleNum,
                description,
                items: context.getters.getItemsToSend,
                user: user.Username,
                pc: settings.PC,
            };
            return axios.post(franchiseTomcatApi + `franchise_stocksent/${storeDB}/saveTransfer`, payload)
                .then(({ data }) => data)
                .catch(err => errorHandler.tomcatError(err, true));
        },
        /**
         * Fetch the transfer slip for the given document id and hand the
         * JSON payload to the global `Printing.printTransfer(...)` helper
         * (the helper is expected to be provided by the runtime / Electron
         * preload - see TODO). Any print failure is swallowed and logged
         * so the caller still receives the server response.
         *
         * TODO: when a `Printing` service export is added to
         * `src/services/`, move this dispatch into that service and have
         * the action call it directly. Until then, this action falls back
         * to a `console.warn` if the global `Printing.printTransfer` is
         * not defined.
         *
         * @param {object} context - Vuex action context
         * @param {object} args
         * @param {string|number} args.id - transfer document id
         * @param {string} args.StoreDB - store database name
         * @returns {Promise<*>} server response body, or `errorHandler.tomcatError` result on failure
         */
        printTransfer: async (context, { id, StoreDB }) => {
            return openUrl(printApi + `franchise_transfer/${StoreDB}/viewRecord/${id}`);
            // return axios.get(franchiseTomcatApi + `franchise_transfer/${StoreDB}/transferLine/${id}`)
            //     .then(({ data }) => {
            //         try {
            //             if (typeof Printing !== 'undefined' && typeof Printing.printTransfer === 'function') {
            //                 Printing.printTransfer(JSON.stringify(data));
            //             } else {
            //                 // TODO: route through the Printing service once it is added.
            //                 console.warn('printTransfer skipped: Printing.printTransfer is not available.');
            //             }
            //         } catch (e) {
            //             console.warn('printTransfer failed:', e);
            //         }
            //         return data;
            //     })
            //     .catch(err => errorHandler.tomcatError(err));
        },
        getStockInBothStores: async (context , payload) => {
            let store = await Storage.getDefaultStore();
            let senderID = store.CustomerID;
            let receiverID = payload.storeID;
            let searchFilter = {
                Plu : payload.Plu,
                Description : payload.Description,
                limit : 50
            }
            return axios.post(franchiseTomcatApi+`franchise/search_stock/match/${senderID}/${receiverID}`,searchFilter)
                .then(({data})=>data)
                .catch(err=>errorHandler.tomcatError(err))
        }
    }
}
